import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Search, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { uploadMediaFile } from './uploader';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MediaPicker = ({ open, onOpenChange, onSelect }) => {
  const adminToken = localStorage.getItem('wvc_admin_token');
  const [media, setMedia] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const fetchMedia = useCallback(async (q = '') => {
    try {
      const res = await axios.get(`${API_URL}/admin/media`, { params: { admin_token: adminToken, search: q || undefined } });
      setMedia(res.data.media || []);
    } catch {
      toast.error('Failed to load media library');
    }
  }, [adminToken]);

  useEffect(() => { if (open) fetchMedia(); }, [open, fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let lastUrl = null;
      for (const file of files) {
        const m = await uploadMediaFile(file, adminToken, setProgress);
        lastUrl = m.url;
      }
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded`);
      await fetchMedia(search);
      if (files.length === 1 && lastUrl && onSelect) {
        onSelect(lastUrl);
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Media Library</DialogTitle>
          <DialogDescription>Pick an image already on your site, or upload a new one. Uploads join the master gallery automatically.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              className="pl-9"
              placeholder="Search images…"
              value={search}
              onChange={e => { setSearch(e.target.value); fetchMedia(e.target.value); }}
              data-testid="media-picker-search"
            />
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="media-picker-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-stone-900 hover:bg-stone-800" data-testid="media-picker-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {uploading ? `Uploading ${progress}%` : 'Upload'}
          </Button>
        </div>
        {media.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-10">No images yet — upload your first one.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-2">
            {media.map(m => (
              <button
                key={m.id}
                onClick={() => { onSelect && onSelect(m.url); onOpenChange(false); }}
                className="group relative aspect-square overflow-hidden rounded-lg border border-stone-200 hover:border-stone-900 transition-colors bg-stone-50"
                title={m.name}
                data-testid={`media-picker-item-${m.id}`}
              >
                <img src={m.url} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/40 flex items-center justify-center transition-colors">
                  <Check className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                </span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
